import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { APP_CONSTANTS } from '@/config';

declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: unknown;
  }
}

window.Pusher = Pusher;

/**
 * The Pusher application the browser subscribes to must be the one the API
 * broadcasts on. This module used to carry both as literals, so every deployment
 * listened to whatever application owned that hardcoded key, no matter what the
 * API's own PUSHER_APP_KEY said: the socket connected, the channel authorized
 * against the right API, and no event ever arrived — with nothing anywhere
 * naming the cause. The values now come from the build environment only, with no
 * fallback, because a wrong realtime endpoint is indistinguishable from a quiet
 * one at runtime.
 *
 * Vite inlines `import.meta.env.VITE_*` at build time, so both names must be
 * declared as build args in `apps/web/Dockerfile` and set by whoever builds the
 * image (see the root `docker-compose.yml` and `apps/web/.env.example`).
 */
export interface EchoEnv {
  readonly VITE_PUSHER_KEY?: string;
  readonly VITE_PUSHER_CLUSTER?: string;
  readonly VITE_API_URL?: string;
}

/**
 * The three build-time values this module needs, read once from `import.meta.env`
 * so the reads are literal (Vite only inlines `import.meta.env.VITE_*` written
 * out in full, and `scripts/ci/check-env-keys.sh` only sees those too).
 */
export const BUILD_ENV: EchoEnv = {
  VITE_PUSHER_KEY: import.meta.env.VITE_PUSHER_KEY,
  VITE_PUSHER_CLUSTER: import.meta.env.VITE_PUSHER_CLUSTER,
  VITE_API_URL: import.meta.env.VITE_API_URL,
};

let warnedAboutMissingConfig = false;

export function missingPusherConfigMessage(missing: readonly string[]): string {
  return `Realtime disabled: this build has no ${missing.join(
    ' and ',
  )}, so no Pusher connection is opened; set them to the API's PUSHER_APP_KEY and PUSHER_APP_CLUSTER when building the web image.`;
}

/**
 * Builds the Echo client, or returns null when the build carries no Pusher
 * configuration. Null is deliberate: `useConnectionHealthState` reports
 * "offline" for it and the realtime consumers subscribe to nothing, which is
 * the honest state. A no-op connector would report a healthy connection that
 * can never deliver an event.
 */
export function createEcho(env: EchoEnv = BUILD_ENV): Echo<'pusher'> | null {
  const key = (env.VITE_PUSHER_KEY ?? '').trim();
  const cluster = (env.VITE_PUSHER_CLUSTER ?? '').trim();

  const missing = [
    key ? null : 'VITE_PUSHER_KEY',
    cluster ? null : 'VITE_PUSHER_CLUSTER',
  ].filter((name): name is string => name !== null);

  if (missing.length > 0) {
    if (!warnedAboutMissingConfig) {
      warnedAboutMissingConfig = true;
      console.warn(missingPusherConfigMessage(missing));
    }
    return null;
  }

  return new Echo({
    broadcaster: 'pusher',
    key,
    cluster,
    forceTLS: true,
    channelAuthorization: {
      endpoint: `${env.VITE_API_URL}/broadcasting/auth`,
      transport: "ajax",
      headersProvider: () => ({
        Authorization: `Bearer ${localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY)}`,
      }),
    },
  });
}

const echo = createEcho();

export default echo;
